// @ts-nocheck
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  ReactFlow,
  Controls,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  MarkerType,
} from 'reactflow';
import { useTheme } from '@mui/material';
import Elk from 'elkjs';
import 'reactflow/dist/style.css';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import CustomBezierEdge from '../../ReactFlow/CustomBezierEdge.jsx';
import {
  MultiEntityNode,
  SingleEntityNode,
} from '../../ReactFlow/NewNodeTypes.jsx';
import useLandScape from '../../../../hooks/ModelInventory/useLandScape';
import { SolyticsBox } from '../../../../package/components';
import { ContextMenus } from '../../ContextMenus.jsx';
import NetworkSkeleton from '../../../Skeleton/NetworkSkeleton';
import {
  getEntityTypeKey,
  getLabelPathFromLabelConfig,
} from '../../../../utils/Utils';
import { DEFAULT_ROUTE_CONFIG } from '../../../../const/defaultRoutesConfig';

const elk = new Elk({
  defaultLayoutOptions: {
    'elk.algorithm': 'layered',
    'elk.direction': 'RIGHT',
    'elk.edgeRouting': 'SPLINES',
    'elk.spacing.edgeNode': '100',
    'elk.spacing.nodeNode': '80',
    'elk.layered.spacing.nodeNodeBetweenLayers': '160',
  },
});

// Performs a DFS from nodeId collecting all reachable nodes and edges in the specified direction ('downstream' or 'upstream').
const traverseGraph = (
  nodeId,
  nodeMap,
  edgeMap,
  direction = 'downstream',
  visited = new Set()
) => {
  if (visited?.has?.(nodeId)) {
    return { nodes: [], edges: [] };
  }
  visited?.add?.(nodeId);

  const resultNodes = [];
  const resultEdges = [];

  const dfs = (id) => {
    const edges = edgeMap?.get?.(id) || [];
    for (const edge of edges) {
      resultEdges.push(edge);
      const nextId = direction === 'downstream' ? edge.target : edge.source;
      const node = nodeMap?.get?.(nextId);
      if (node && !visited?.has?.(node.id)) {
        visited?.add?.(node.id);
        resultNodes.push(node);
        dfs(node.id);
      }
    }
  };

  dfs(nodeId);
  return { nodes: resultNodes, edges: resultEdges };
};

// Groups bidirectional edges on one handle and unidirectional on another.
// Dynamically picks which handle (upper 30% vs lower 70%) each type gets
// based on vertical positions of connected nodes, preventing criss-crossing.
const assignHandles = (newNodes, newEdges) => {
  if (!newNodes || !newEdges) return [];
  const nodeMap = new Map(newNodes.map((n) => [n.id, n]));

  // Clone edges to avoid parameter property mutation
  const processedEdges = newEdges.map((edge) => ({ ...edge }));

  // Accumulates handle overrides (targetHandle / sourceHandle) per edge,
  // so we can apply them immutably at the end without mutating forEach parameters.
  const edgeHandleMap = new Map();
  const assignHandle = (edge, key, value) => {
    const prev = edgeHandleMap.get(edge) || {};
    edgeHandleMap.set(edge, { ...prev, [key]: value });
  };

  // Helper: check if an edge is bidirectional
  const checkBidirectional = (edge) => edge.isBidirectional === true;

  // Collect edges grouped by (nodeId + side) for incoming and outgoing
  const incomingGroups = new Map();
  const outgoingGroups = new Map();

  processedEdges.forEach((edge) => {
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);
    if (!sourceNode || !targetNode) return;

    // Group incoming edges by target node + side
    const isLeft = sourceNode.x < targetNode.x;
    const inKey = `${edge.target}-${isLeft ? 'L' : 'R'}`;
    if (!incomingGroups.has(inKey)) {
      incomingGroups.set(inKey, { isLeft, items: [] });
    }
    incomingGroups.get(inKey).items.push({ edge, connectedY: sourceNode.y });

    // Group outgoing edges by source node + side
    const isRight = targetNode.x > sourceNode.x;
    const outKey = `${edge.source}-${isRight ? 'R' : 'L'}`;
    if (!outgoingGroups.has(outKey)) {
      outgoingGroups.set(outKey, { isRight, items: [] });
    }
    outgoingGroups.get(outKey).items.push({ edge, connectedY: targetNode.y });
  });

  // Helper: compute average Y of a group
  const avgY = (arr) =>
    arr.reduce((sum, i) => sum + i.connectedY, 0) / arr.length;

  // Assign targetHandles: group bi and uni edges, pick upper/lower to avoid crossing
  incomingGroups.forEach(({ isLeft, items }) => {
    const biEdges = items.filter((i) => checkBidirectional(i.edge));
    const uniEdges = items.filter((i) => !checkBidirectional(i.edge));

    // Determine which type goes to upper handle (30%) vs lower handle (70%)
    // Default: uni→upper(30%), bi→lower(70%)
    // Swap when bi sources are vertically higher (lower Y) to prevent crossing
    let biGoesUpper = false;
    if (biEdges.length > 0 && uniEdges.length > 0) {
      biGoesUpper = avgY(biEdges) < avgY(uniEdges);
    }

    const upperHandle = isLeft ? 'target-uni' : 'source-uni';
    const lowerHandle = isLeft ? 'target-bi' : 'source-bi';

    biEdges.forEach((i) => {
      assignHandle(
        i.edge,
        'targetHandle',
        biGoesUpper ? upperHandle : lowerHandle
      );
    });
    uniEdges.forEach((i) => {
      assignHandle(
        i.edge,
        'targetHandle',
        biGoesUpper ? lowerHandle : upperHandle
      );
    });
  });

  // Assign sourceHandles: same logic for outgoing side
  outgoingGroups.forEach(({ isRight, items }) => {
    const biEdges = items.filter((i) => checkBidirectional(i.edge));
    const uniEdges = items.filter((i) => !checkBidirectional(i.edge));

    let biGoesUpper = false;
    if (biEdges.length > 0 && uniEdges.length > 0) {
      biGoesUpper = avgY(biEdges) < avgY(uniEdges);
    }

    const upperHandle = isRight ? 'source-uni' : 'target-uni';
    const lowerHandle = isRight ? 'source-bi' : 'target-bi';

    biEdges.forEach((i) => {
      assignHandle(
        i.edge,
        'sourceHandle',
        biGoesUpper ? upperHandle : lowerHandle
      );
    });
    uniEdges.forEach((i) => {
      assignHandle(
        i.edge,
        'sourceHandle',
        biGoesUpper ? lowerHandle : upperHandle
      );
    });
  });

  return processedEdges.map((edge) => {
    const overrides = edgeHandleMap.get(edge);
    return overrides ? { ...edge, ...overrides } : edge;
  });
};

// Builds ReactFlow nodes and edges from entity graph data, computes ELK layout positions,
// and applies initial visibility rules for cluster nodes beyond level 1.
const createGraphLayout = async (
  relevantEntities,
  handleNodeEntityClick,
  handleSetTemplate,
  handleEntitySelect,
  strokeColor
) => {
  const nodes = [];
  const edges = [];

  relevantEntities.forEach((entity) => {
    const entity_nodes = entity?.graph_data?.Nodes || [];
    const entity_edges = entity?.graph_data?.Edges || [];

    const nodeMap = new Map(entity_nodes?.map((n) => [n.id, n]));

    const upstreamEdgeMap = new Map();
    entity_edges.forEach((edge) => {
      if (!upstreamEdgeMap.has(edge.target)) {
        upstreamEdgeMap.set(edge.target, []);
      }
      upstreamEdgeMap.get(edge.target).push(edge);
    });

    const downstreamEdgeMap = new Map();
    entity_edges.forEach((edge) => {
      if (!downstreamEdgeMap.has(edge.source)) {
        downstreamEdgeMap.set(edge.source, []);
      }
      downstreamEdgeMap.get(edge.source).push(edge);
    });

    const hiddenNodes = new Set();
    const hiddenEdges = new Set();

    entity_nodes.forEach((node) => {
      if (
        node?.type === 'main_entity_node' ||
        (node?.type === 'cluster_node' &&
          node?.level !== undefined &&
          Math.abs(node.level) <= 1) ||
        (node?.associated_entities &&
          Object.keys(node?.associated_entities || {}).length === 1 &&
          Object.values(node?.associated_entities || {})[0].length === 1)
      ) {
        return;
      }

      if (node?.level > 0) {
        const { nodes: downstreamNodes, edges: downstreamEdges } =
          traverseGraph(node?.id, nodeMap, downstreamEdgeMap, 'downstream');
        downstreamNodes.forEach((n) => hiddenNodes.add(n?.id));
        downstreamEdges.forEach((e) => hiddenEdges.add(e?.id));
      } else {
        const { nodes: upstreamNodes, edges: upstreamEdges } = traverseGraph(
          node?.id,
          nodeMap,
          upstreamEdgeMap,
          'upstream'
        );
        upstreamNodes.forEach((n) => hiddenNodes.add(n?.id));
        upstreamEdges.forEach((e) => hiddenEdges.add(e?.id));
      }
    });

    entity_nodes.forEach((node) => {
      if (
        node?.type === 'cluster_node' &&
        node?.level !== undefined &&
        Math.abs(node.level) > 1
      ) {
        hiddenNodes.add(node.id);
        (upstreamEdgeMap.get(node.id) || []).forEach((e) =>
          hiddenEdges.add(e.id)
        );
      }
    });

    const mainEntityNode = entity_nodes.find(
      (n) => n?.type === 'main_entity_node'
    );
    const mainEntityUniqueId = mainEntityNode?.data?.properties?.unique_id;
    const mainEntityEntityType = mainEntityNode?.entity_type;

    entity_nodes.forEach((node) => {
      const isMain = node?.type === 'main_entity_node';
      const width = isMain ? 133 : 262;
      const height = isMain ? 50 : 110;
      const parentEdges = upstreamEdgeMap.get(node.id) || [];
      const parentNode = nodeMap.get(parentEdges[0]?.source);
      const parentRelationName =
        parentNode?.type === 'cluster_node'
          ? parentNode?.relation?.name
          : undefined;

      let updatedAssociatedEntities = undefined;
      if (node?.associated_entities) {
        updatedAssociatedEntities = {};
        Object.entries(node.associated_entities).forEach(([template, list]) => {
          updatedAssociatedEntities[template] = list.map((ent) => {
            const calculatedCount = entity_nodes.filter(
              (n) =>
                n.type !== 'cluster_node' &&
                (Array.isArray(n.parent_id)
                  ? n.parent_id.includes(ent.id)
                  : n.parent_id === ent.id)
            ).length;
            return {
              ...ent,
              data: {
                ...ent.data,
                properties: {
                  ...ent.data?.properties,
                  linked_entity_count: calculatedCount,
                },
              },
            };
          });
        });
      }

      nodes.push({
        ...node,
        data: {
          ...node?.data,
          level: node?.level,
          ...(node?.entity_type && { entity_type: node?.entity_type }),
          ...(node?.request_type && { request_type: node?.request_type }),
          ...(updatedAssociatedEntities && {
            associated_entities: updatedAssociatedEntities,
          }),
          ...(node?.relation && { relation: node?.relation }),
          ...(node?.type === 'cluster_node' && {
            ...(mainEntityUniqueId && { mainEntityUniqueId }),
            ...(mainEntityEntityType && { mainEntityEntityType }),
            ...(parentRelationName && { parentRelationName }),
          }),
          id: node?.id,
          handleNodeEntityClick,
          handleSetTemplate,
          handleEntitySelect,
          filteredByParentEntityIds: [],
          selectedEntityId: null,
        },
        width,
        height,
        hidden: hiddenNodes.has(node?.id),
      });
    });

    entity_edges.forEach((edge) => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);
      // Resolve edge relation dynamically based on hierarchy direction (downstream vs upstream)
      let relation;
      if (targetNode?.level > 0) {
        relation = targetNode?.relation;
      } else if (sourceNode?.level < 0) {
        relation = sourceNode?.relation;
      } else {
        relation = targetNode?.relation || sourceNode?.relation;
      }
      // Determine if the edge relation is bidirectional
      const isBidirectional = relation?.is_bidirectional === true;
      const currentStrokeColor = strokeColor;

      edges.push({
        ...edge,
        isBidirectional,
        type: 'custom',
        data: {
          relation,
        },
        animated: false,
        style: { stroke: currentStrokeColor },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: currentStrokeColor,
          width: 25,
          height: 25,
        },
        markerStart: isBidirectional
          ? {
              type: MarkerType.ArrowClosed,
              color: currentStrokeColor,
              width: 25,
              height: 25,
            }
          : undefined,
        hidden: hiddenEdges.has(edge?.id),
      });
    });
  });

  const newGraph = await elk.layout({ id: 'root', children: nodes, edges });

  const newNodes = newGraph?.children?.map((el) => ({
    ...el,
    position: { x: el?.x, y: el?.y },
  }));
  // Map ELK layout edges back to their original ReactFlow edge object properties,
  // ensuring we retain custom flags (e.g. isBidirectional) and layout coordinates
  const newEdges = newGraph?.edges?.map((elkEdge) => {
    const originalEdge = edges.find((e) => e.id === elkEdge.id);
    return {
      ...originalEdge,
      ...elkEdge,
    };
  });
  // Distribute and sort handles vertically to eliminate edge criss-crossing
  const finalEdges = assignHandles(newNodes, newEdges);

  return { newNodes, newEdges: finalEdges };
};

const nodeTypes = {
  cluster_node: MultiEntityNode,
  main_entity_node: SingleEntityNode,
};

const edgeTypes = {
  custom: CustomBezierEdge,
};

// ReactFlow graph that renders the relationship and lineage of a single entity,
// with entity selection, downstream/upstream filtering, and node expand/collapse support.
const RelationshipGraph = memo(({ entityId, entityType }) => {
  const [loading, setLoading] = useState(false);

  const { getLandScapeSingleEntity } = useLandScape();
  const theme = useTheme();
  const { fitView } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);

  const edgeOptions = useMemo(
    () => ({
      animated: false,
      style: { stroke: theme?.palette?.landscape?.handleColor },
    }),
    [theme]
  );

  // Resolves the entity's dynamic route and opens it in a new browser tab.
  const navigateToModel = useCallback((node) => {
    let resolvedEntityType, resolvedUniqueId, resolvedRequestType;

    if (node?.type === 'main_entity_node') {
      console.log('[LOG] node: ', node);
      resolvedEntityType = node?.data?.entity_type;
      resolvedUniqueId = node?.data?.properties?.unique_id;
      resolvedRequestType = node?.data?.properties?.request_type;
    } else {
      resolvedEntityType =
        node?.data?.properties?.entity_type || node?.entity_type;
      resolvedUniqueId = node?.data?.properties?.unique_id;
      resolvedRequestType =
        node?.data?.properties?.request_type || node?.request_type;
    }

    if (!resolvedEntityType || !resolvedUniqueId) {
      return;
    }

    const entityTypeKey = getEntityTypeKey(
      resolvedEntityType,
      resolvedRequestType
    );
    const dynamicEntityPath = getLabelPathFromLabelConfig(
      DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || ``,
      'path',
      DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
    );

    window.open(
      `/${dynamicEntityPath}/${resolvedUniqueId}`,
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  const nodeOperationMenus = useMemo(
    () => [
      {
        label: 'Open Entity',
        id: 'open',
        icon: OpenInNewIcon,
        onClick: ({ props }) => {
          navigateToModel(props);
        },
      },
    ],
    [navigateToModel]
  );

  // Placeholder for template-change events; actual template state is managed inside each node component.
  const handleSetTemplate = useCallback((_idValueObject) => {
    // template change is handled inside node components
  }, []);

  // Propagates an entity selection from a cluster node: filters downstream nodes by the selected entity
  // and auto-selects the matching parent entity in directly upstream cluster nodes.
  const handleEntitySelect = useCallback(
    (nodeId, selectedEntityId, parentEntityIds) => {
      const currentEdges = edgesRef.current || [];

      const downstreamEdgeMap = new Map();
      const upstreamEdgeMap = new Map();
      currentEdges.forEach((edge) => {
        if (!downstreamEdgeMap.has(edge.source)) {
          downstreamEdgeMap.set(edge.source, []);
        }
        downstreamEdgeMap.get(edge.source).push(edge);
        if (!upstreamEdgeMap.has(edge.target)) {
          upstreamEdgeMap.set(edge.target, []);
        }
        upstreamEdgeMap.get(edge.target).push(edge);
      });

      const downstreamNodeIds = new Set(
        (downstreamEdgeMap.get(nodeId) || []).map((e) => e.target)
      );
      const upstreamNodeIds = new Set(
        (upstreamEdgeMap.get(nodeId) || []).map((e) => e.source)
      );

      // Returns the id of the entity in upstream node n whose id appears in parentEntityIds.
      const findMatchingParentEntityId = (n) => {
        if (!selectedEntityId || !parentEntityIds?.length) return null;
        const allEntities = Object.values(
          n.data?.associated_entities || {}
        ).flat();
        const match = allEntities.find((e) => parentEntityIds.includes(e.id));
        return match ? match.id : null;
      };

      const downstreamHiddenMap = new Map();
      (nodesRef.current || []).forEach((n) => {
        if (!downstreamNodeIds.has(n.id)) return;
        if (n.type !== 'cluster_node') return;
        const nodeLevel = n.data?.level;
        if (nodeLevel === undefined || Math.abs(nodeLevel) <= 1) return;
        const allEntities = Object.values(
          n.data?.associated_entities || {}
        ).flat();
        const hasMatchingChildren = selectedEntityId
          ? allEntities.some((e) => e.parent_id?.includes(selectedEntityId))
          : false;
        downstreamHiddenMap.set(n.id, !hasMatchingChildren);
      });

      // Find the single main_entity_node id so we can skip traversal through it.
      // The main entity node is a permanent anchor; selecting an upstream entity
      // should not cascade into hiding downstream nodes on the other side.
      const mainEntityNodeId =
        (nodesRef.current || []).find((n) => n.type === 'main_entity_node')
          ?.id || null;

      // Collect all nodes that are transitively downstream of the direct
      // downstream nodes (grandchildren, great-grandchildren, etc.).
      // These must be hidden when the parent entity selection changes,
      // otherwise orphaned nodes remain visible on the canvas.
      // IMPORTANT: Do NOT traverse through main_entity_node to prevent
      // upstream selections from hiding downstream cluster nodes.
      const deeperDownstreamNodeIds = new Set();
      {
        const queue = [...downstreamNodeIds];
        while (queue.length > 0) {
          const currentId = queue.shift();
          // Stop traversal at main_entity_node — it is a permanent anchor
          if (currentId === mainEntityNodeId) continue;
          const childEdges = downstreamEdgeMap.get(currentId) || [];
          for (const edge of childEdges) {
            if (
              !deeperDownstreamNodeIds.has(edge.target) &&
              !downstreamNodeIds.has(edge.target) &&
              edge.target !== nodeId
            ) {
              deeperDownstreamNodeIds.add(edge.target);
              queue.push(edge.target);
            }
          }
        }
      }

      setNodes((prevNodes) => {
        const updated = prevNodes.map((n) => {
          if (n.id === nodeId) {
            return {
              ...n,
              data: { ...n.data, selectedEntityId: selectedEntityId || null },
            };
          }
          // Skip main_entity_node — it is a permanent anchor and should not have
          // its entity list filtered or selection reset by upstream changes.
          if (downstreamNodeIds.has(n.id) && n.id !== mainEntityNodeId) {
            const isHigherLevelCluster = downstreamHiddenMap.has(n.id);
            const hidden = isHigherLevelCluster
              ? downstreamHiddenMap.get(n.id)
              : n.hidden;
            return {
              ...n,
              hidden,
              data: {
                ...n.data,
                filteredByParentEntityIds: selectedEntityId
                  ? [selectedEntityId]
                  : [],
                selectedEntityId: null,
              },
            };
          }
          // Deeper downstream nodes (grandchildren+): always hide and reset state
          if (deeperDownstreamNodeIds.has(n.id)) {
            return {
              ...n,
              hidden: true,
              data: {
                ...n.data,
                filteredByParentEntityIds: [],
                selectedEntityId: null,
              },
            };
          }
          if (upstreamNodeIds.has(n.id)) {
            const matchId = findMatchingParentEntityId(n);
            if (matchId) {
              return { ...n, data: { ...n.data, selectedEntityId: matchId } };
            }
          }
          return n;
        });
        nodesRef.current = updated;
        return updated;
      });

      setEdges((prevEdges) => {
        const updated = prevEdges.map((e) => {
          if (
            downstreamNodeIds.has(e.target) &&
            downstreamHiddenMap.has(e.target)
          ) {
            return { ...e, hidden: downstreamHiddenMap.get(e.target) };
          }
          // Hide edges to deeper downstream nodes (grandchildren+)
          if (deeperDownstreamNodeIds.has(e.target)) {
            return { ...e, hidden: true };
          }
          return e;
        });
        edgesRef.current = updated;
        return updated;
      });
    },
    [setNodes, setEdges]
  );

  // Recursively traverses the graph from node collecting connected single-entity nodes and edges;
  // stops at multi-entity cluster nodes when they are being shown (not hidden).
  const deepTraverseNodes = useCallback(
    (
      node,
      nodeMap,
      edgeMap,
      hidden,
      direction = 'downstream',
      visited = new Set()
    ) => {
      const associated_entity = node?.associated_entities || {};
      const multiEntityNode =
        Object.values(associated_entity || {}).flat().length > 1;
      if (visited?.has?.(node?.id) || (multiEntityNode && !hidden)) {
        return { nodes: [], edges: [] };
      }
      visited?.add?.(node?.id);

      const resultNodes = [];
      const resultEdges = [];

      const dfs = (id) => {
        const childEdges = edgeMap?.get?.(id) || [];
        for (const edge of childEdges) {
          resultEdges.push(edge);
          const nextId = direction === 'downstream' ? edge.target : edge.source;
          const targetNode = nodeMap?.get?.(nextId);
          if (targetNode && !visited?.has?.(targetNode.id)) {
            visited?.add?.(targetNode.id);
            resultNodes.push(targetNode);
            dfs(targetNode.id);
          }
        }
      };
      dfs(node?.id);

      return { nodes: resultNodes, edges: resultEdges };
    },
    []
  );

  // Toggles visibility of nodes and edges connected to a cluster node when it is expanded or collapsed.
  const handleNodeEntityClick = useCallback(
    ({ hidden, cluster_node }) => {
      const hiddenNodeIds = new Set();
      const hiddenEdgeIds = new Set();
      const currentEdges = edgesRef.current;
      const currentNodes = nodesRef.current;
      const nodeMap = new Map(currentNodes?.map((n) => [n.id, n]));

      const upstreamEdgeMap = new Map();
      currentEdges.forEach((edge) => {
        if (!upstreamEdgeMap.has(edge.target)) {
          upstreamEdgeMap.set(edge.target, []);
        }
        upstreamEdgeMap.get(edge.target).push(edge);
      });

      const downstreamEdgeMap = new Map();
      currentEdges.forEach((edge) => {
        if (!downstreamEdgeMap.has(edge.source)) {
          downstreamEdgeMap.set(edge.source, []);
        }
        downstreamEdgeMap.get(edge.source).push(edge);
      });

      const connectedEdges =
        cluster_node?.level > 0
          ? downstreamEdgeMap.get(cluster_node?.node_id)
          : upstreamEdgeMap.get(cluster_node?.node_id);

      const directionKey = cluster_node?.level > 0 ? 'target' : 'source';
      const connectedNodes = [];
      connectedEdges?.forEach((edge) => {
        const targetNode = currentNodes?.find(
          (n) => n.id === edge[directionKey]
        );
        if (targetNode) {
          connectedNodes.push(targetNode);
        }
      });

      const filteredConnectedNodes = connectedNodes.filter((n) => {
        const firstEntity = Object.values(
          n?.data?.associated_entities || {}
        )[0]?.[0];
        return firstEntity?.parent_id?.includes(cluster_node?.id);
      });

      filteredConnectedNodes?.forEach((node) => {
        hiddenNodeIds.add(node?.id);
        if (cluster_node?.level > 0) {
          const { nodes: nds, edges: edg } = deepTraverseNodes(
            node,
            nodeMap,
            downstreamEdgeMap,
            hidden,
            'downstream'
          );
          nds?.forEach((n) => hiddenNodeIds.add(n?.id));
          edg?.forEach((e) => hiddenEdgeIds.add(e?.id));
        } else {
          const { nodes: nds, edges: edg } = deepTraverseNodes(
            node,
            nodeMap,
            upstreamEdgeMap,
            hidden,
            'upstream'
          );
          nds?.forEach((n) => hiddenNodeIds.add(n?.id));
          edg?.forEach((e) => hiddenEdgeIds.add(e?.id));
        }
        const foundEdge = connectedEdges?.find(
          (n) => n?.[directionKey] === node?.id
        );
        if (foundEdge) {
          hiddenEdgeIds.add(foundEdge.id);
        }
      });

      setNodes((prevNodes) => {
        const updated = prevNodes?.map((n) =>
          hiddenNodeIds.has(n?.id) ? { ...n, hidden } : n
        );
        nodesRef.current = updated;
        return updated;
      });

      setEdges((prevEdges) => {
        const updated = prevEdges?.map((e) =>
          hiddenEdgeIds.has(e?.id) ? { ...e, hidden } : e
        );
        edgesRef.current = updated;
        return updated;
      });
    },
    [deepTraverseNodes, setNodes, setEdges]
  );

  useEffect(() => {
    if (!entityId || !entityType) return;
    setLoading(true);
    getLandScapeSingleEntity({ entity_id: entityId, entity_type: entityType })
      .then(async (res) => {
        if (res?.data) {
          const entityData = {
            ...res.data,
            entity_id: res.data?.entity_id,
            entity_type: entityType,
          };
          const { newNodes, newEdges } = await createGraphLayout(
            [entityData],
            handleNodeEntityClick,
            handleSetTemplate,
            handleEntitySelect,
            theme?.palette?.landscape?.handleColor
          );
          setNodes(newNodes);
          setEdges(newEdges);
          nodesRef.current = newNodes;
          edgesRef.current = newEdges;
        }
      })
      .finally(() => setLoading(false));
  }, [
    entityId,
    entityType,
    getLandScapeSingleEntity,
    handleNodeEntityClick,
    handleSetTemplate,
    handleEntitySelect,
    theme,
  ]);

  useEffect(() => {
    if (nodes.length) {
      fitView({
        padding: 0.2,
        includeHiddenNodes: false,
        minZoom: 0.5,
        maxZoom: 1.2,
        duration: 0,
      });
    }
  }, [nodes.length, fitView]);

  //adjust relationship and lineage graph when screen  is resize
  useEffect(() => {
    let timeout;

    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        fitView({
          padding: 0.2,
          includeHiddenNodes: false,
          minZoom: 0.5,
          maxZoom: 1.2,
          duration: 0,
        });
      }, 200);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, [fitView]);

  if (loading) {
    return <NetworkSkeleton />;
  }

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={edgeOptions}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          includeHiddenNodes: false,
          minZoom: 0.5,
          maxZoom: 1.2,
          duration: 0,
        }}
        snapToGrid={true}
        snapGrid={[20, 20]}
        proOptions={{ hideAttribution: true }}
      >
        <Controls />
      </ReactFlow>
      <ContextMenus menuId="node-rightClick" menus={nodeOperationMenus} />
    </>
  );
});

RelationshipGraph.propTypes = {
  entityId: PropTypes.string.isRequired,
  entityType: PropTypes.string.isRequired,
};

const RelationshipLinkageTab = ({ entityId, entityType }) => {
  return (
    <SolyticsBox
      sx={{ height: 'calc(100vh - 300px)', maxHeight: '100%', width: '100%' }}
    >
      <ReactFlowProvider>
        <RelationshipGraph entityId={entityId} entityType={entityType} />
      </ReactFlowProvider>
    </SolyticsBox>
  );
};

RelationshipLinkageTab.propTypes = {
  entityId: PropTypes.string.isRequired,
  entityType: PropTypes.string.isRequired,
};

export default React.memo(RelationshipLinkageTab);
